/**
 * Structured logging utility with environment-aware output
 * Provides consistent logging patterns across the application
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  component?: string;
  operation?: string;
  userId?: string;
  [key: string]: any;
}

// Detect development environment
const isDevelopment = 
  typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || 
   window.location.hostname === '127.0.0.1' ||
   window.location.hostname.includes('127.0.0.1'));

/**
 * Environment-aware logger that provides clean console output in development
 * and structured logs in production
 */
class Logger {
  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? ` ${JSON.stringify(context)}` : '';
    return `[${timestamp}] [${level.toUpperCase()}] ${message}${contextStr}`;
  }

  debug(message: string, context?: LogContext) {
    if (isDevelopment) {
      console.debug(`🔍 [Debug] ${message}`, context || '');
    }
  }

  info(message: string, context?: LogContext) {
    if (isDevelopment) {
      console.info(`ℹ️ [Info] ${message}`, context || '');
    } else {
      // Production: minimal structured logging
      console.log(this.formatMessage('info', message, context));
    }
  }

  warn(message: string, context?: LogContext) {
    console.warn(`⚠️ [Warning] ${message}`, context || '');
  }

  error(message: string, error?: Error, context?: LogContext) {
    const errorContext = {
      ...context,
      errorMessage: error?.message,
      errorStack: isDevelopment ? error?.stack : undefined
    };
    console.error(`❌ [Error] ${message}`, errorContext);
  }

  /**
   * Log expected authentication events (not errors)
   * These are normal operations that should not alarm developers
   */
  authEvent(message: string, context?: LogContext) {
    if (isDevelopment) {
      console.info(`🔐 [Auth] ${message}`, context || '');
    }
  }

  /**
   * Log security-related events (access blocks, auth checks)
   * These are informational, not errors
   */
  securityEvent(message: string, context?: LogContext) {
    if (isDevelopment) {
      console.info(`🛡️ [Security] ${message}`, context || '');
    }
  }
}

export const logger = new Logger();

/**
 * Check if an error is expected during authentication cleanup
 */
export function isExpectedAuthError(error: any): boolean {
  if (!error) return false;

  const errorMessage = typeof error === 'string' ? error : error.message || '';
  const errorString = errorMessage.toLowerCase();

  // Expected patterns during sign-out
  return (
    errorString.includes('refresh_token') ||
    errorString.includes('token invalidated') ||
    errorString.includes('session expired') ||
    errorString.includes('invalid jwt') ||
    errorString.includes('jwt expired') ||
    errorString.includes('bad jwt') ||
    (errorString.includes('400') && sessionStorage.getItem('signing_out') === 'true')
  );
}

/**
 * Check if an error is a CORS error on protected route (expected after logout)
 */
export function isExpectedCorsError(error: any): boolean {
  if (!error) return false;

  const errorMessage = typeof error === 'string' ? error : error.message || '';
  const errorString = errorMessage.toLowerCase();

  return errorString.includes('cors') && 
         (errorString.includes('dashboard') || 
          errorString.includes('api') ||
          sessionStorage.getItem('signing_out') === 'true');
}
